import React, { Component } from "react";
import { Button } from "reactstrap";
import moment from "moment";

import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import "./ag-theme-ela.css";

import { FetchData, FetchFile } from "./utils/DataFetch";
import { GetCookie, openFileInNewTab } from "./utils/Common";
import { TranslationHistoryForm, historyTypeLabel } from "./TranslationHistoryForm";

import "./Translation.css";

const newEntry = (jobId, type) => ({
  EventType: "insert",
  JobHistoryId: null,
  JobId: jobId,
  JobHistoryType: type,
  Description: "",
  FileName: "",
  FileLocation: "",
  CreateDateTime: null,
  CreateByUserId: null,
  IsEditableFlg: true,
});

// JobHistory stores UTC; shown in local time, as the work order history grid does.
const toLocal = (value) => (value ? moment.utc(value).local() : null);

const FileCell = ({ data, context }) =>
  data?.fileName ? (
    <button
      type="button"
      className="tr-grid-link"
      onClick={() => context.openFile(data)}
      aria-label={`Open attachment ${data.fileName}`}
      title={data.fileName}
    >
      📎 {data.fileName}
    </button>
  ) : null;

const ActionsCell = ({ data, context }) =>
  context.readOnly || data?.isEditableFlg === false ? null : (
    <span className="tr-grid-actions">
      <button
        type="button"
        className="tr-grid-link"
        onClick={() => context.edit(data)}
        aria-label={`Edit ${historyTypeLabel(data.jobHistoryType)} entry`}
      >
        Edit
      </button>
      <button
        type="button"
        className="tr-grid-link tr-grid-link-danger"
        onClick={() => context.remove(data)}
        aria-label={`Delete ${historyTypeLabel(data.jobHistoryType)} entry`}
      >
        Delete
      </button>
    </span>
  );

const columns = [
  {
    headerName: "Created On",
    field: "createDateTime",
    width: 150,
    sort: "desc",
    valueFormatter: (p) => toLocal(p.value)?.format("MM/DD/YYYY hh:mm A") ?? "",
    comparator: (a, b) => (toLocal(a)?.valueOf() ?? 0) - (toLocal(b)?.valueOf() ?? 0),
  },
  { headerName: "Created By", field: "createdBy", width: 140 },
  {
    headerName: "Type",
    field: "jobHistoryType",
    width: 170,
    valueFormatter: (p) => historyTypeLabel(p.value),
  },
  {
    headerName: "Detail",
    field: "description",
    flex: 1,
    minWidth: 220,
    tooltipField: "description",
  },
  { headerName: "File", field: "fileName", width: 190, cellRenderer: FileCell, tooltipField: "fileName" },
  { headerName: "", colId: "actions", width: 110, cellRenderer: ActionsCell, sortable: false, filter: false },
];

const defaultColDef = {
  resizable: true,
  sortable: true,
  filter: true,
};

/**
 * Translation History: the job's JobHistory rows (notes and attachments) in a grid like the
 * work order's job history, labelled for translations. Stored in the same JobHistory table.
 */
export class TranslationHistory extends Component {
  static displayName = TranslationHistory.name;

  constructor(props) {
    super(props);
    this.user = JSON.parse(GetCookie("user"));
    this.state = { entries: [], loading: true, editing: null };
  }

  componentDidMount() {
    this.load();
  }

  load = () => {
    FetchData("/getjobhistory", "POST", this.user.token, { JobId: this.props.jobId }).then((data) => {
      this.setState({ entries: Array.isArray(data) ? data : [], loading: false });
    });
  };

  announce = (message) => this.props.onMessage?.(message);

  edit = (row) =>
    this.setState({
      editing: {
        EventType: "update",
        JobHistoryId: row.jobHistoryId,
        JobId: row.jobId,
        JobHistoryType: row.jobHistoryType,
        Description: row.description ?? "",
        FileName: row.fileName ?? "",
        FileLocation: row.fileLocation ?? "",
        CreateDateTime: row.createDateTime,
        CreateByUserId: row.createByUserId,
        IsEditableFlg: row.isEditableFlg,
      },
    });

  remove = (row) => {
    if (!window.confirm(`Delete this ${historyTypeLabel(row.jobHistoryType).toLowerCase()} entry?`)) return;

    FetchData("/deletejobhistory", "POST", this.user.token, {
      JobHistoryId: row.jobHistoryId,
      UserId: this.user.userid,
    }).then((data) => {
      if (data === "Success") {
        this.announce("History entry deleted.");
        this.load();
      } else {
        this.announce(data || "Could not delete the history entry.");
      }
    });
  };

  openFile = (row) => {
    FetchFile("/getjobhistoryfile", "POST", this.user.token, {
      JobHistoryId: row.jobHistoryId,
    }).then((blob) => {
      if (!blob || blob.size === 0) {
        this.announce(`Could not open ${row.fileName}.`);
        return;
      }
      openFileInNewTab(blob, row.fileName);
    });
  };

  onSaved = (message) => {
    this.setState({ editing: null });
    this.announce(message);
    this.load();
  };

  render() {
    const { entries, loading, editing } = this.state;
    const { jobId, readOnly } = this.props;

    return (
      <section className="tr-section" aria-labelledby="tr-sec-history">
        <div className="tr-section-header">
          <h3 id="tr-sec-history" className="tr-section-title">
            Translation History
            <span className="tr-column-count ms-2" aria-label={`${entries.length} entries`}>
              {entries.length}
            </span>
          </h3>
          {!readOnly && (
            <div className="d-flex gap-2">
              <Button size="sm" color="primary" outline onClick={() => this.setState({ editing: newEntry(jobId, "Job Note") })}>
                + Note
              </Button>
              <Button size="sm" color="primary" outline onClick={() => this.setState({ editing: newEntry(jobId, "Attachment") })}>
                + Attachment
              </Button>
            </div>
          )}
        </div>

        <div className="ag-theme-balham tr-history-grid" style={{ width: "100%", height: 200 }}>
          <AgGridReact
            columnDefs={columns}
            rowData={loading ? null : entries}
            defaultColDef={defaultColDef}
            rowHeight={26}
            headerHeight={28}
            context={{ openFile: this.openFile, edit: this.edit, remove: this.remove, readOnly }}
            onRowDoubleClicked={(p) => !readOnly && p.data?.isEditableFlg !== false && this.edit(p.data)}
            enableBrowserTooltips={true}
            overlayNoRowsTemplate='<span class="tr-hint">No notes or attachments yet.</span>'
            overlayLoadingTemplate='<span class="tr-hint">Loading history…</span>'
          />
        </div>

        {editing && (
          <TranslationHistoryForm
            model={editing}
            onSaved={this.onSaved}
            onCancel={() => this.setState({ editing: null })}
          />
        )}
      </section>
    );
  }
}
